/*
 * Purpose: Autopilot final plan — vault unlock, connections, run live
 * Last modified: 2026-06-28 | Agent
 * Completeness: 94
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Blocks,
  CheckCircle2,
  ChevronDown,
  FlaskConical,
  Map,
  Play,
  RotateCcw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
import { PlanReadinessPanel } from '@/components/PlanReadinessPanel';
import { GmailConnectorPanel } from '@/components/GmailConnectorPanel';
import { LinkedInConnectorPanel } from '@/components/LinkedInConnectorPanel';
import { McpConnectorPanel } from '@/components/McpConnectorPanel';
import { ConnectorLibraryPanel } from '@/components/ConnectorLibraryPanel';
import { ComposeFlowMapPreview } from '@/components/intent/ComposeFlowMapPreview';
import { ComposePlanRowCard } from '@/components/intent/ComposePlanRowCard';
import { ContextualError } from '@/components/errors/ContextualError';
import { SuperIntentInterpreter } from '@/components/intent/SuperIntentInterpreter';
import type { DryRunReport, OutcomePlan } from '@/services/autoblocks/types';
import { auditPlanReadiness } from '@/services/autoblocks/planReadiness';
import { attachWorkflowPreview } from '@/services/autoblocks/workflowAssembler';
import { useSecretStore } from '@/stores/secretStore';
import { REMEDIATION } from '@/lib/remediationRoutes';
import { PRODUCT_TERMS } from '@/lib/productTerms';
import { getBlockTypeLabel } from '@/lib/blockTypes';
import { cn } from '@/lib/utils';

interface ComposeFinalPlanPanelProps {
  outcomeText: string;
  plan: OutcomePlan;
  dryRunReport: DryRunReport | null;
  isRunningLive: boolean;
  error: string | null;
  onOutcomeChange: (text: string) => void;
  onPlanUpdate: (plan: OutcomePlan) => void;
  onRunLive: () => void;
  onSaveOnly: () => void;
  onRetryDryRun: () => void;
  onStartOver: () => void;
}

export function ComposeFinalPlanPanel({
  outcomeText,
  plan,
  dryRunReport,
  isRunningLive,
  error,
  onOutcomeChange,
  onPlanUpdate,
  onRunLive,
  onSaveOnly,
  onRetryDryRun,
  onStartOver,
}: ComposeFinalPlanPanelProps) {
  const navigate = useNavigate();
  const secrets = useSecretStore((s) => s.secrets);
  const isUnlocked = useSecretStore((s) => s.isUnlocked);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editOutcome, setEditOutcome] = useState(false);

  const readiness = useMemo(
    () => auditPlanReadiness(plan),
    [plan, secrets, isUnlocked]
  );

  const workflowPreview = useMemo(
    () => plan.workflowPreview ?? attachWorkflowPreview(plan).workflowPreview,
    [plan]
  );

  const showLinkedInPanel =
    plan.steps.some((s) => s.subtype === 'mcp_tool' && /\blinkedin/i.test(String(s.config.mcpGoal ?? s.blockIntent ?? ''))) ||
    plan.connectors.some((c) => c.id.includes('linkedin')) ||
    /\blinkedin\b/i.test(plan.rawOutcome);

  const showGmailPanel =
    plan.gmailInboxId ||
    plan.useGmailInbound ||
    plan.steps.some((s) => s.subtype === 'gmail_inbound');

  const showMcpPanel =
    plan.resolvedConnectors?.some((c) => c.connectorId === 'ab.mcp') ||
    plan.steps.some((s) => s.subtype === 'mcp_tool') ||
    plan.connectors.some((c) => c.id.includes('mcp')) ||
    /\bmcp\b|model context protocol/i.test(plan.rawOutcome);

  const canRunLive =
    Boolean(dryRunReport?.readyForReview) &&
    readiness.allRequiredReady &&
    !isRunningLive;

  const blockCount = plan.steps.length || workflowPreview?.nodes.length || 0;

  return (
    <Card className="border-0 shadow-none ring-1 ring-primary/20 overflow-hidden">
      <CardHeader className="pb-3 px-4 pt-4 bg-gradient-to-b from-ab-green-mist/40 to-transparent dark:from-ab-green-forest/20">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <CheckCircle2 className="h-5 w-5 text-ab-green-deep dark:text-ab-green-glow shrink-0" />
              <CardTitle className="text-lg sm:text-xl">Your plan is ready</CardTitle>
              {dryRunReport?.passed && (
                <Badge variant="default" className="text-[10px]">
                  <FlaskConical className="h-3 w-3 mr-1" />
                  Dry run passed
                </Badge>
              )}
            </div>
            <CardDescription className="text-sm">{plan.interpretedGoal}</CardDescription>
          </div>
          <Badge variant="secondary" className="shrink-0">
            {blockCount} {blockCount === 1 ? PRODUCT_TERMS.block.toLowerCase() : PRODUCT_TERMS.blocks.toLowerCase()}
          </Badge>
        </div>

        <blockquote className="mt-3 text-xs sm:text-sm text-muted-foreground border-l-2 border-ab-green-sea/50 pl-3 line-clamp-3">
          &ldquo;{outcomeText}&rdquo;
        </blockquote>

        <div className="flex flex-wrap gap-2 mt-3">
          <Button variant="ghost" size="sm" className="h-8" onClick={() => setEditOutcome((v) => !v)}>
            {editOutcome ? 'Hide outcome' : 'Edit outcome'}
          </Button>
          <Button variant="ghost" size="sm" className="h-8" onClick={onStartOver}>
            <RotateCcw className="h-3.5 w-3.5 mr-1" />
            Start over
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-5 px-4 pb-5">
        {editOutcome && (
          <SuperIntentInterpreter
            variant="compose"
            value={outcomeText}
            onChange={onOutcomeChange}
            onSubmit={() => setEditOutcome(false)}
            showExamples={false}
          />
        )}

        {error && (
          <ContextualError
            message={error}
            action={{ label: 'Start over', onClick: onStartOver }}
            secondaryAction={{ label: 'Open Setup', href: REMEDIATION.setup }}
          />
        )}

        <div id="compose-final-readiness" className="scroll-mt-4">
          <PlanReadinessPanel plan={plan} onPlanUpdate={onPlanUpdate} />
        </div>

        {showLinkedInPanel && (
          <LinkedInConnectorPanel onConnected={() => onPlanUpdate(plan)} />
        )}

        {showMcpPanel && (
          <McpConnectorPanel compact onConnected={() => onPlanUpdate(plan)} />
        )}

        {showGmailPanel && (
          <GmailConnectorPanel
            plan={plan}
            onInboxReady={(inboxId, postUrl) => {
              onPlanUpdate(
                attachWorkflowPreview({
                  ...plan,
                  gmailInboxId: inboxId,
                  gmailPostUrl: postUrl,
                  useGmailInbound: true,
                })
              );
            }}
          />
        )}

        {plan.resolvedConnectors && plan.resolvedConnectors.length > 0 && (
          <ConnectorLibraryPanel plan={plan} />
        )}

        {!readiness.allRequiredReady && readiness.requiredTotal > 0 && (
          <ContextualError
            message={
              isUnlocked
                ? `${readiness.requiredTotal - readiness.requiredReady} connection(s) still needed — complete setup above, then run live.`
                : 'Unlock your vault to save keys and connect services before a live run.'
            }
            action={
              !isUnlocked
                ? {
                    label: 'Focus unlock',
                    onClick: () => {
                      const field = document.getElementById('vault-inline-pass');
                      if (field) field.focus();
                      else document.getElementById('compose-final-readiness')?.scrollIntoView({ behavior: 'smooth' });
                    },
                  }
                : undefined
            }
            secondaryAction={{ label: 'Open Connect', href: REMEDIATION.connect }}
          />
        )}

        {dryRunReport && !dryRunReport.passed && (
          <ContextualError
            message="Dry run found issues — fix blockers or retry simulation before going live."
            action={{ label: 'Retry dry run', onClick: onRetryDryRun }}
          />
        )}

        <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="outline" size="sm" className="w-full justify-between h-9">
              <span className="flex items-center gap-2">
                <Blocks className="h-4 w-4" />
                Plan details
              </span>
              <ChevronDown className={cn('h-4 w-4 transition-transform', detailsOpen && 'rotate-180')} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-3 space-y-3">
            {workflowPreview && workflowPreview.nodes.length > 0 && (
              <ComposePlanRowCard
                title="Flow map preview"
                description={`${workflowPreview.nodes.length} blocks wired`}
                icon={<Map />}
                contentClassName="!mt-2"
              >
                <ComposeFlowMapPreview nodes={workflowPreview.nodes} edges={workflowPreview.edges} />
              </ComposePlanRowCard>
            )}
            {plan.steps.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {plan.steps.map((step, i) => (
                  <ComposePlanRowCard
                    key={step.id}
                    title={`${i + 1}. ${step.label}`}
                    description={step.rationale}
                    icon={<Blocks className="h-4 w-4 ui-icon" />}
                    contentClassName="!mt-2"
                  >
                    <Badge variant="outline" className="text-[10px]">
                      {getBlockTypeLabel(step.type, step.subtype)}
                    </Badge>
                  </ComposePlanRowCard>
                ))}
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>

        <Separator />

        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <Button
            size="lg"
            className="w-full sm:flex-1 ui-lift-subtle"
            disabled={!canRunLive}
            onClick={onRunLive}
          >
            <Play className="h-4 w-4 mr-2 fill-current" />
            {isRunningLive ? 'Starting live run…' : 'Run live'}
          </Button>
          <Button
            variant="secondary"
            size="lg"
            className="w-full sm:w-auto"
            disabled={!dryRunReport?.readyForReview || isRunningLive}
            onClick={onSaveOnly}
          >
            Save to My Flows
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="w-full sm:w-auto"
            onClick={() => navigate(REMEDIATION.mission)}
          >
            <Map className="h-4 w-4 mr-1" />
            Open map
          </Button>
        </div>

        {!dryRunReport?.readyForReview && (
          <p className="text-xs text-muted-foreground text-center">
            Waiting for dry run — this usually completes automatically.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
